import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MdDelete } from 'react-icons/md';

import DepotMobile from '../DepotMobile';
import Layout from '../Layout';
import {
  appliquerReponse, consignerReponse, lireConfrontation,
  lireCourrierAssureur, majPieceDemandee, supprimerReponse, televerserPiece,
} from '../api';
import ChampDate from '../../components/ChampDate/ChampDate';

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

const TYPES = [
  { code: 'ACCUSE', libelle: 'Accusé de réception' },
  { code: 'VISITE', libelle: "Visite d'un expert ou d'un inspecteur" },
  { code: 'PIECES', libelle: 'Demande de pièces' },
  { code: 'ESTIMATION',
    libelle: "Estimation de l'expert — n'engage pas la compagnie" },
  { code: 'PROPOSITION', libelle: 'Proposition chiffrée de la compagnie' },
  { code: 'ACCORD', libelle: 'Accord de la compagnie' },
  { code: 'REFUS', libelle: 'Refus de prise en charge' },
  { code: 'AUTRE', libelle: 'Autre' },
];

const CANAUX = [
  { code: 'COURRIEL', libelle: 'Courriel' },
  { code: 'COURRIER', libelle: 'Courrier' },
  { code: 'TELEPHONE', libelle: 'Téléphone' },
  { code: 'VISITE', libelle: 'Sur place' },
  { code: 'PORTAIL', libelle: 'Portail de la compagnie' },
];

const COULEURS = {
  ACCORD: 'success', REFUS: 'error', PROPOSITION: 'warning',
  ESTIMATION: 'default', PIECES: 'info', VISITE: 'info',
  DECLARATION: 'default', ACCUSE: 'default',
};

const VIDE = {
  type: 'PROPOSITION', canal: 'COURRIEL',
  recue_le: new Date().toISOString().slice(0, 10),
  montant_propose: '', resume: '', prevue_le: '', libelles_demandes: '',
};

/** Un événement du dossier, dans l'ordre où il est arrivé. */
function Evenement({ evenement, onPiece, onSupprimer }) {
  const e = evenement;
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="baseline"
                 flexWrap="wrap" useFlexGap>
            <Chip size="small" color={COULEURS[e.type] || 'default'}
                  label={e.libelle} />
            <Typography variant="body2" color="text.secondary">
              {dateCourte(e.date)}
              {e.canal ? ` · ${e.canal}` : ''}
              {e.auteur ? ` · ${e.auteur}` : ''}
            </Typography>
          </Stack>

          {e.montant !== null && e.montant !== undefined && (
            <>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {euros(e.montant)}
              </Typography>
              {e.engage === false && (
                <Typography variant="caption" color="text.secondary">
                  Ce chiffre n'engage pas la compagnie.
                </Typography>
              )}
            </>
          )}
          {e.resume && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>{e.resume}</Typography>
          )}
          {e.prevue_le && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Visite prévue le {dateCourte(e.prevue_le)}.
            </Typography>
          )}

          {e.pieces?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {e.pieces.map((p) => (
                <FormControlLabel
                  key={p.id || p.libelle}
                  control={
                    <Checkbox size="small" checked={p.statut === 'FOURNIE'}
                              onChange={(ev) => onPiece(p, ev.target.checked)} />
                  }
                  label={
                    <Typography variant="body2">
                      {p.libelle}
                      {p.echeance ? ` — pour le ${dateCourte(p.echeance)}` : ''}
                    </Typography>
                  }
                />
              ))}
            </Box>
          )}
        </Box>

        {e.id && (
          <IconButton aria-label="Retirer cet événement"
                      onClick={() => onSupprimer(e)}>
            <MdDelete />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * Dixième étape : ce que l'assureur répond, et ce que ça vaut.
 *
 * Le but n'est pas de trouver un désaccord. Deux chiffrages sérieux du même
 * dommage diffèrent couramment de 10 % : l'écran dit où l'écart sort de cette
 * marge, et dit aussi quand la compagnie a raison.
 */
export default function ReponseAssureur({ expertiseId, onRetour, onEtape }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [formulaire, setFormulaire] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [lecture, setLecture] = useState(null);
  // Ce que le courrier dit autrement que le dossier : proposé, jamais imposé.
  const [divergences, setDivergences] = useState([]);
  const [qrOuvert, setQrOuvert] = useState(false);

  const recharger = useCallback(() => {
    lireConfrontation(expertiseId)
      .then(setDonnees)
      .catch(() => setErreur("Impossible de charger la réponse de l'assureur."));
  }, [expertiseId]);

  useEffect(() => { recharger(); }, [recharger]);

  const enregistrer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const { libelles_demandes: libelles, ...reste } = formulaire;
      await consignerReponse(expertiseId, {
        ...reste,
        montant_propose: reste.montant_propose === '' ? null
          : reste.montant_propose,
        prevue_le: reste.prevue_le || null,
        libelles_demandes: libelles
          ? libelles.split('\n').map((l) => l.trim()).filter(Boolean) : [],
      });
      setFormulaire(null);
      recharger();
    } catch {
      setErreur("L'enregistrement n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  // Deuxième voie : déposer le courrier et laisser l'OCR puis le modèle
  // proposer. Rien n'est enregistré avant validation.
  const deposerCourrier = async (fichiers) => {
    if (!fichiers.length) return;
    setOccupe(true);
    setErreur('');
    try {
      const piece = await televerserPiece(expertiseId, fichiers[0], 'lettre');
      const propose = await lireCourrierAssureur(piece.id);
      setLecture({ ...propose, document: piece.id });
    } catch (e) {
      setErreur(e?.response?.data?.detail
        || "La lecture du courrier n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  const validerLecture = async () => {
    setOccupe(true);
    try {
      const enregistree = await appliquerReponse(expertiseId, {
        ...lecture, recue_le: lecture.date_courrier
          || new Date().toISOString().slice(0, 10),
      });
      setLecture(null);
      setDivergences(enregistree.divergences || []);
      recharger();
    } catch {
      setErreur("L'enregistrement n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  if (!donnees && !erreur) {
    return (
      <Layout titre="La réponse de votre assureur" onEtape={onEtape}>
        <CircularProgress size={28} />
      </Layout>
    );
  }

  const s = donnees?.silence;
  const t = donnees?.totaux;

  return (
    <Layout
      titre="La réponse de votre assureur"
      introduction="Ce que la compagnie annonce, et ce que sa proposition vaut face à votre réclamation."
      onEtape={onEtape}
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button component="label" variant="outlined" disabled={occupe}>
            Déposer un courrier reçu
            <input type="file" hidden accept="image/*,application/pdf"
                   onChange={(e) => deposerCourrier([...e.target.files])} />
          </Button>
          {/* Une proposition d'expert arrive souvent sur papier, remise en
              main propre : la photographier est le chemin le plus court. */}
          <Button component="label" variant="outlined" disabled={occupe}>
            Le photographier
            <input type="file" hidden accept="image/*" capture="environment"
                   onChange={(e) => deposerCourrier([...e.target.files])} />
          </Button>
          <Button variant="outlined" disabled={occupe}
                  onClick={() => setQrOuvert(true)}>
            Avec mon téléphone
          </Button>
          <Button variant="contained"
                  onClick={() => setFormulaire({ ...VIDE })}>
            Consigner une réponse
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

      {divergences.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}
               onClose={() => setDivergences([])}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Le courrier dit autre chose que votre dossier
          </Typography>
          {divergences.map((d, i) => (
            <Typography key={i} variant="body2">
              · {d.quoi} — au dossier : « {d.au_dossier} », dans le courrier :
              {' '}« {d.dans_le_courrier} »
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Rien n'a été remplacé. Corrigez vous-même si le courrier a raison.
          </Typography>
        </Alert>
      )}

      <Stack spacing={3}>
        {s?.a_relancer && (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {s.jours} jours sans réponse depuis le {dateCourte(s.depuis)}.
            </Typography>
            <Typography variant="body2">
              {s.delai_contractuel
                ? `Le délai de ${s.delai} jours prévu par vos conditions est dépassé — ${s.delai_source}.`
                : `${s.delai_source}. Nous retenons ${s.delai} jours comme repère : vérifiez ce que votre contrat prévoit avant toute mise en demeure.`}
            </Typography>
          </Alert>
        )}

        {donnees?.estimations?.length > 0 && !donnees.compagnie_engagee && (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Une estimation n'est pas une offre
            </Typography>
            {donnees.estimations.map((e) => (
              <Typography key={e.id} variant="body2">
                · {dateCourte(e.date)}
                {e.auteur ? ` — ${e.auteur}` : ''} : {euros(e.montant)}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Un expert vient toujours estimer le dommage, et son chiffre sert
              de base à la discussion — mais il n'engage pas la compagnie.
              Attendez sa position écrite avant de considérer l'affaire réglée.
            </Typography>
          </Alert>
        )}

        {donnees?.en_attente?.length > 0 && (
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Ce qui attend une suite de votre part
            </Typography>
            {donnees.en_attente.map((a) => (
              <Typography key={a.id} variant="body2">
                · {a.libelle}
                {a.prevue_le ? ` — le ${dateCourte(a.prevue_le)}` : ''}
                {a.pieces?.length > 0 ? ` : ${a.pieces.join(', ')}` : ''}
              </Typography>
            ))}
          </Alert>
        )}

        {t && Number(t.accorde) > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={0.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">Vous réclamez</Typography>
                <Typography variant="body1">{euros(t.reclame)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">La compagnie propose</Typography>
                <Typography variant="body1">{euros(t.accorde)}</Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2"
                          color={t.dans_la_marge ? 'text.secondary' : 'error'}>
                {donnees.phrase}
              </Typography>
            </Stack>
          </Paper>
        )}

        {donnees?.a_examiner?.length > 0 && (
          <Box>
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              Les postes à examiner
            </Typography>
            <Stack spacing={1.5}>
              {donnees.a_examiner.map((p) => (
                <Paper key={p.poste_id} variant="outlined" sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between"
                         spacing={2}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {p.titre}
                    </Typography>
                    <Typography variant="body1">
                      {euros(p.reclame)} → {euros(p.accorde)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {p.phrase}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {donnees?.coherents?.length > 0 && (
          <Alert severity="success">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Ce sur quoi la compagnie a raison
            </Typography>
            {donnees.coherents.map((p) => (
              <Typography key={p.poste_id} variant="body2">
                · {p.titre} — {euros(p.accorde)} contre {euros(p.reclame)}
                {' '}réclamés, soit {p.pourcentage} %. L'écart tient dans la
                {' '}marge d'évaluation.
              </Typography>
            ))}
          </Alert>
        )}

        {donnees?.sans_reponse?.length > 0 && (
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Postes sur lesquels la compagnie ne s'est pas prononcée
            </Typography>
            {donnees.sans_reponse.map((p) => (
              <Typography key={p.poste_id} variant="body2">
                · {p.titre} — {euros(p.reclame)}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Ne pas répondre n'est pas refuser : relancez plutôt que de
              contester.
            </Typography>
          </Alert>
        )}

        <Box>
          <Typography variant="h3" sx={{ mb: 1.5 }}>Le fil du dossier</Typography>
          <Stack spacing={1.5}>
            {donnees?.chronologie?.map((e, i) => (
              <Evenement
                key={e.id || `d-${i}`}
                evenement={e}
                onPiece={async (piece, fournie) => {
                  await majPieceDemandee(piece.id, {
                    statut: fournie ? 'FOURNIE' : 'A_FOURNIR',
                    fournie_le: fournie
                      ? new Date().toISOString().slice(0, 10) : null,
                  });
                  recharger();
                }}
                onSupprimer={async (ev) => {
                  await supprimerReponse(ev.id); recharger();
                }}
              />
            ))}
            {donnees?.chronologie?.length === 0 && (
              <Alert severity="info">
                Rien n'est encore arrivé. Déposez le courrier reçu, ou
                consignez la réponse à la main.
              </Alert>
            )}
          </Stack>
        </Box>
      </Stack>

      <DepotMobile
        ouvert={qrOuvert}
        onFermer={() => { setQrOuvert(false); recharger(); }}
        expertiseId={expertiseId}
        categorie="lettre"
      />

      {/* Saisie manuelle */}
      <Dialog open={formulaire !== null} onClose={() => setFormulaire(null)}
              fullWidth maxWidth="sm">
        <DialogTitle>Consigner une réponse</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Nature de la réponse"
                       value={formulaire?.type ?? 'PROPOSITION'}
                       onChange={(e) => setFormulaire((f) => ({
                         ...f, type: e.target.value }))}>
              {TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Reçue par" fullWidth
                         value={formulaire?.canal ?? 'COURRIEL'}
                         onChange={(e) => setFormulaire((f) => ({
                           ...f, canal: e.target.value }))}>
                {CANAUX.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.libelle}</MenuItem>
                ))}
              </TextField>
              <ChampDate label="Reçue le" fullWidth
                         InputLabelProps={{ shrink: true }}
                         value={formulaire?.recue_le ?? ''}
                         onChange={(e) => setFormulaire((f) => ({
                           ...f, recue_le: e.target.value }))} />
            </Stack>

            {formulaire?.type === 'VISITE' && (
              <ChampDate label="Visite prévue le"
                         InputLabelProps={{ shrink: true }}
                         value={formulaire?.prevue_le ?? ''}
                         onChange={(e) => setFormulaire((f) => ({
                           ...f, prevue_le: e.target.value }))} />
            )}

            {['PROPOSITION', 'ACCORD', 'ESTIMATION'].includes(formulaire?.type) && (
              <TextField label="Montant proposé" type="number"
                         value={formulaire?.montant_propose ?? ''}
                         onChange={(e) => setFormulaire((f) => ({
                           ...f, montant_propose: e.target.value }))}
                         helperText={formulaire?.type === 'ESTIMATION'
                           ? "Le chiffre de l'expert. Il ne lie pas la compagnie."
                           : "Le montant global annoncé par la compagnie."} />
            )}

            {formulaire?.type === 'PIECES' && (
              <TextField label="Pièces réclamées" multiline minRows={3}
                         value={formulaire?.libelles_demandes ?? ''}
                         onChange={(e) => setFormulaire((f) => ({
                           ...f, libelles_demandes: e.target.value }))}
                         helperText="Une par ligne." />
            )}

            <TextField label="Ce qui est dit" multiline minRows={2}
                       value={formulaire?.resume ?? ''}
                       onChange={(e) => setFormulaire((f) => ({
                         ...f, resume: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormulaire(null)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer} disabled={occupe}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ce que la lecture propose — à relire avant d'enregistrer */}
      <Dialog open={lecture !== null} onClose={() => setLecture(null)}
              fullWidth maxWidth="sm">
        <DialogTitle>Ce que nous avons lu dans ce courrier</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Relisez avant d'enregistrer : rien n'est retenu tant que vous
              n'avez pas validé.
            </Alert>
            {lecture?.avertissement && (
              <Alert severity="warning">{lecture.avertissement}</Alert>
            )}

            <TextField select label="Nature" value={lecture?.type ?? 'AUTRE'}
                       onChange={(e) => setLecture((l) => ({
                         ...l, type: e.target.value }))}>
              {TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <ChampDate label="Date du courrier" fullWidth
                         InputLabelProps={{ shrink: true }}
                         value={lecture?.date_courrier ?? ''}
                         onChange={(e) => setLecture((l) => ({
                           ...l, date_courrier: e.target.value }))} />
              <TextField label="Montant proposé" type="number" fullWidth
                         value={lecture?.montant_propose ?? ''}
                         onChange={(e) => setLecture((l) => ({
                           ...l, montant_propose: e.target.value }))} />
            </Stack>

            <TextField label="Référence du sinistre"
                       value={lecture?.reference_sinistre ?? ''}
                       onChange={(e) => setLecture((l) => ({
                         ...l, reference_sinistre: e.target.value }))}
                       helperText="Elle ne remplacera pas celle que vous avez déjà saisie." />

            {lecture?.contacts?.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Personnes relevées chez la compagnie
                </Typography>
                {lecture.contacts.map((c, i) => (
                  <Typography key={i} variant="body2">
                    · {c.nom}{c.fonction ? ` — ${c.fonction}` : ''}
                    {c.email ? ` · ${c.email}` : ''}
                    {c.telephone ? ` · ${c.telephone}` : ''}
                  </Typography>
                ))}
                <Typography variant="caption" color="text.secondary">
                  Elles rejoindront vos intervenants.
                </Typography>
              </Box>
            )}

            {lecture?.pieces_demandees?.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Pièces réclamées
                </Typography>
                {lecture.pieces_demandees.map((p, i) => (
                  <Typography key={i} variant="body2">· {p}</Typography>
                ))}
              </Box>
            )}

            <TextField label="Résumé" multiline minRows={2}
                       value={lecture?.resume ?? ''}
                       onChange={(e) => setLecture((l) => ({
                         ...l, resume: e.target.value }))} />

            {lecture?.texte_lu && (
              <TextField label="Texte lu dans le document" multiline
                         minRows={3} maxRows={8} value={lecture.texte_lu}
                         InputProps={{ readOnly: true }}
                         helperText="Pour vérifier ce que la machine a lu, et pas seulement ce qu'elle en a conclu." />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLecture(null)}>Annuler</Button>
          <Button variant="contained" onClick={validerLecture} disabled={occupe}>
            Enregistrer cette réponse
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
